import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

export interface FilterOption {
  value: string;
  label: string;
  dotColor?: string;
}

interface StatusFilterDropdownProps {
  value: string;
  allValue?: string;
  allLabel?: string;
  options: FilterOption[];
  onChange: (value: string) => void;
  placeholder?: string;
}

const StatusFilterDropdown: React.FC<StatusFilterDropdownProps> = ({
  value,
  allValue = "all",
  allLabel = "All Status",
  options,
  onChange,
  placeholder = "Filter by status",
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const selected = options.find((o) => o.value === value);

  const choose = (v: string) => {
    onChange(v);
    setOpen(false);
  };

  return (
    <div className="filter-select" ref={ref}>
      <button
        type="button"
        className={`filter-select-trigger ${value !== allValue ? "is-active" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        title={placeholder}
      >
        {selected ? (
          <>
            {selected.dotColor && (
              <span
                className="filter-select-dot"
                style={{ background: selected.dotColor }}
              />
            )}
            <span>{selected.label}</span>
          </>
        ) : (
          <span className="filter-select-placeholder">{allLabel}</span>
        )}
        <ChevronDown
          size={16}
          className={`filter-select-chevron ${open ? "open" : ""}`}
        />
      </button>

      {open && (
        <div className="filter-select-menu">
          <button
            type="button"
            className={`filter-select-option ${value === allValue ? "selected" : ""}`}
            onClick={() => choose(allValue)}
          >
            <span>{allLabel}</span>
          </button>
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              className={`filter-select-option ${value === o.value ? "selected" : ""}`}
              onClick={() => choose(o.value)}
            >
              <span>{o.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default StatusFilterDropdown;
